import { Dtos } from "@inrev/common";
import { type Editor } from "@tiptap/react";
import { DateTime } from "luxon";
import { memo, type ReactNode, useEffect, useRef } from "react";
import { match } from "ts-pattern";
import { HtmlContentDisplay } from "./HtmlContentDisplay";
import { StackedCard } from "./StackedCard";

export type CommentsProps = {
	comments: Dtos.Comment.Get.Response[] | undefined;
	commentsLoading: boolean;
	composer: ReactNode;
};

export const Comment = memo(({ comment }: { comment: Dtos.Comment.Get.Response }) => {
	const now = DateTime.now();
	const commentDate = DateTime.fromISO(comment.createdAt);

	const startOfToday = now.startOf("day");
	const startOfYesterday = startOfToday.minus({ days: 1 });
	const endOfYesterday = startOfToday.minus({ seconds: 1 });

	const displayDate = match(commentDate)
		.when(
			(date) => date >= startOfToday,
			(date) => `Today at ${date.toFormat("h:mm a")}`,
		)
		.when(
			(date) => date >= startOfYesterday && date <= endOfYesterday,
			(date) => `Yesterday at ${date.toFormat("h:mm a")}`,
		)
		.otherwise((date) => `${date.toFormat("EEEE MMM dd, yyyy")} at ${date.toFormat("h:mm a")}`);

	return (
		<div className="flex flex-col shrink-0 px-[12px] space-y-[12px]">
			<div className="flex items-center space-x-[4px]">
				<span className="text-[13px] font-[550] text-gray-700">
					{comment.fromUserFirstName} {comment.fromUserLastName}
					<span className="text-[12px] ml-[6px]">
						{comment.fromUserType === "admin" ? "(inRev)" : ""}
					</span>
				</span>
				<span className="text-gray-400 text-[11px] font-medium">{displayDate}</span>
			</div>
			<div className="text-[14px] px-[20px] py-[18px] bg-white border border-gray-300 rounded">
				<HtmlContentDisplay content={comment.message} />
			</div>
		</div>
	);
});

export type CommentEditorProps = {
	editor: Editor;
	commentCount: number;
	createCommentLoading: boolean;
	handleCreateComment?: (message: string) => void;
};

const NEAR_BOTTOM_THRESHOLD_PX = 300;

export const Comments = ({ comments, commentsLoading, composer }: CommentsProps) => {
	const messageContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = messageContainerRef.current;
		if (!el || !comments?.length) return;
		const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		if (el.scrollTop === 0 || distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX) {
			el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
		}
	}, [comments?.length]);

	return (
		<StackedCard className="flex flex-col w-full">
			{!!comments && comments.length > 0 && (
				<div
					ref={messageContainerRef}
					className="w-full h-fit border-b pt-[20px] pb-[20px] border-b-gray-200 bg-gray-100/60 rounded-t-md max-h-[450px] overflow-y-auto"
				>
					{commentsLoading && (
						<span className="w-full text-center text-[14px] italic">Loading comments...</span>
					)}
					{!!comments && comments.length > 0 && (
						<div className="flex flex-col w-full shrink-0 h-fit px-[12px] last:mb-[20px] space-y-[24px]">
							{!!comments &&
								comments.map((comment, index) => <Comment key={index} comment={comment} />)}
						</div>
					)}
				</div>
			)}
			{composer}
		</StackedCard>
	);
};
